import { NoSsr } from "@mui/base";
import { useRouter } from "next/router";
import type { FunctionComponent } from "react";
import { graphql, useFragment } from "react-relay";

import { BaseLink } from "shared/components/common/base-link";
import { getUrlPathWithoutQuery, isValidUrl } from "shared/lib/utils";

import { useTopicType } from "scmp-app/components/topic/hooks";
import type { topicHeaderRelatedLinksTopic$key } from "scmp-app/queries/__generated__/topicHeaderRelatedLinksTopic.graphql";

import { Container, Label, StyledNavBar, StyledNavBarItem } from "./styles";

type Props = {
  className?: string;
  reference: topicHeaderRelatedLinksTopic$key;
};

export const TopicHeaderRelatedLinks: FunctionComponent<Props> = ({ className, reference }) => {
  const topic = useFragment(
    graphql`
      fragment topicHeaderRelatedLinksTopic on Topic {
        ...hooksTopic
        relatedLink {
          title
          url
        }
        relatedTopics {
          name
          urlAlias
        }
      }
    `,
    reference,
  );

  const { isSuperTopic = false } = useTopicType(topic);

  const { asPath } = useRouter();

  const renderRelatedTopics = () => {
    const relatedTopics = topic?.relatedTopics ?? [];
    return relatedTopics?.map((topic, index) => {
      const isSelected = topic?.urlAlias === getUrlPathWithoutQuery(asPath);
      return topic?.urlAlias ? (
        <NoSsr key={index}>
          <StyledNavBarItem $isSelected={isSelected}>
            <BaseLink pathname={topic.urlAlias}>{topic?.name}</BaseLink>
          </StyledNavBarItem>
        </NoSsr>
      ) : null;
    });
  };

  const renderRelatedLinks = () =>
    topic?.relatedLink?.map((link, index) => {
      if (!link?.url) return null;

      const url = link?.url ?? "/";
      const pathname = isValidUrl(url) ? new URL(url).pathname : link?.url;
      const isSelected = pathname === getUrlPathWithoutQuery(asPath);

      return (
        <NoSsr key={index}>
          <StyledNavBarItem $isSelected={isSelected}>
            <BaseLink pathname={link?.url}>{link?.title}</BaseLink>
          </StyledNavBarItem>
        </NoSsr>
      );
    });

  if (
    (isSuperTopic && (topic?.relatedLink ?? []).length === 0) ||
    (!isSuperTopic && (topic?.relatedTopics ?? []).length === 0)
  )
    return null;

  return (
    <Container className={className}>
      <StyledNavBar $isSuperTopic={isSuperTopic}>
        <>
          {!isSuperTopic && (
            <StyledNavBarItem>
              <Label>Related Topics:</Label>
            </StyledNavBarItem>
          )}
          {isSuperTopic ? renderRelatedLinks() : renderRelatedTopics()}
        </>
      </StyledNavBar>
    </Container>
  );
};

TopicHeaderRelatedLinks.displayName = "TopicHeaderRelatedLinks";
